import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';
import { ENV_VARIABLES } from '../config';
import { TokenReadView } from '../types/types';
import { ENDPOINTS } from './endpoints';

export enum HttpStatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  Ok = 200,
  Created = 201,
  NoContent = 204,
  ServerError = 500
}

// Allow runtime base-URL override for tests
let __baseOverride: string | undefined;

const computeBaseURL = () => {
  const forced = process.env.FORCE_LOCAL_API === '1' ? 'http://localhost:8080' : undefined;

  // Get ENV_VARIABLES.apiBaseUrl
  // In browser (yarn start), process.env.REACT_APP_DEV_API_BASE_URL is injected by webpack
  // In production, window.config.API_BASE_URL is used
  const envApiBaseUrl = ENV_VARIABLES.apiBaseUrl;

  const raw = __baseOverride ?? forced ?? envApiBaseUrl ?? process.env.API_BASE_URL;

  // Guard against undefined/null or string "undefined"/"null" - use sensible default
  if (!raw || raw === 'undefined' || raw === 'null' || String(raw).trim() === '') {
    // In browser, try to infer from window.location or use default
    if (typeof window !== 'undefined') {
      // If running on localhost:3000 (dev server), assume backend is on :8080
      if (window.location.origin.includes('localhost:3000')) {
        return 'http://localhost:8080/v1/';
      }
      // Otherwise, try same origin (for production builds)
      return `${window.location.origin}/v1/`;
    }
    // Fallback for Node.js/test environments
    return 'http://localhost:8080/v1/';
  }

  const base = String(raw).replace(/\/+$/, '');
  return `${base}/v1/`;
};

// Exported axios instance for testing
let axiosInstance: AxiosInstance | null = null;

export const getAxiosInstance = (): AxiosInstance => {
  if (!axiosInstance) {
    // Allow timeout to be configured via environment variable, default to 30 seconds
    // Can be overridden per-request if needed
    const defaultTimeout = process.env.REACT_APP_HTTP_TIMEOUT
      ? parseInt(process.env.REACT_APP_HTTP_TIMEOUT, 10)
      : 30000; // 30 seconds default (increased from 15s)

    axiosInstance = axios.create({ baseURL: computeBaseURL(), timeout: defaultTimeout });
    axiosInstance.interceptors.request.use(injectToken, (error) => Promise.reject(error));
    axiosInstance.interceptors.response.use((response: AxiosResponse<any>) => response, HandleAuthError);
  }
  return axiosInstance;
};

/**
 * Builds an absolute URL under /v2/ instead of the default /v1/ base — for endpoints that have
 * been migrated individually while the rest of the API is still on v1. Pass a path with no
 * leading slash, e.g. buildV2Url('organisations').
 */
export const buildV2Url = (path: string): string => {
  const v1Base = getAxiosInstance().defaults.baseURL ?? computeBaseURL();
  return `${v1Base.replace(/\/v1\/?$/, '/v2/')}${path}`;
};

// New: allow tests to swap target at runtime if needed
export const __resetHttpForTests = (newBaseUrl?: string) => {
  __baseOverride = newBaseUrl?.replace(/\/+$/, '');
  axiosInstance = null; // next getAxiosInstance() rebuilds using env
};

// ---- auth failure callback wiring ----
// Allows React components to register a handler for auth failures
// without creating a dependency on React in the HTTP layer
let onAuthFailure: ((error: AxiosError) => void) | null = null;

export const setAuthFailureHandler = (handler: (error: AxiosError) => void) => {
  onAuthFailure = handler;
};
// -------------------------------------

const injectToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = localStorage.getItem('tractech_token');
  const sessionId = localStorage.getItem('tractech_session_id');
  const headers = new AxiosHeaders(config.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Only send X-Session-Id if sessionId exists and backend CORS allows it
  // Backend must include 'x-session-id' in Access-Control-Allow-Headers
  // Temporarily disabled in development if backend CORS doesn't support it
  // TODO: Enable once backend CORS is configured to allow 'x-session-id' header
  if (sessionId && process.env.REACT_APP_ENABLE_SESSION_ID_HEADER === 'true') {
    headers.set('X-Session-Id', sessionId);
  }
  config.headers = headers;
  return config;
};

// Centralized refresh token function with concurrency protection
// Prevents multiple simultaneous refresh calls
let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem('tractech_refresh_token');
    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token'));
    }

    refreshPromise = axios
      .post<TokenReadView>(`${computeBaseURL()}${ENDPOINTS.auth}`, { refreshToken })
      .then(({ data }) => {
        localStorage.setItem('tractech_token', data.token);
        if (data.refreshToken) localStorage.setItem('tractech_refresh_token', data.refreshToken);
        if (data.sessionId) localStorage.setItem('tractech_session_id', data.sessionId);
        return data.token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const HandleAuthError = (error: AxiosError & { config?: RetriableConfig }): any => {
  const originalConfig = error.config as RetriableConfig | undefined;
  const status = error.response?.status;

  // Helper to notify registered auth failure handler
  const notifyAuthFailure = (err: AxiosError) => {
    if (onAuthFailure) {
      onAuthFailure(err);
    }
  };

  // Only attempt refresh if:
  // 1. Status is 401 (Unauthorized)
  // 2. We have a config (request can be retried)
  // 3. We haven't already retried this request (_retry guard prevents infinite loops)
  if (status === HttpStatusCode.Unauthorized && originalConfig && !originalConfig._retry) {
    originalConfig._retry = true; // Set flag BEFORE refresh to prevent infinite loops

    return refreshAccessToken()
      .then((newToken) => {
        // Clone headers to avoid mutating frozen objects
        const hdrs = new AxiosHeaders(originalConfig.headers);
        hdrs.set('Authorization', `Bearer ${newToken}`);
        originalConfig.headers = hdrs;
        return getAxiosInstance().request(originalConfig);
      })
      .catch((refreshError: AxiosError) => {
        // Refresh failed - clear tokens and notify handler to trigger logout
        localStorage.removeItem('tractech_token');
        localStorage.removeItem('tractech_refresh_token');
        localStorage.removeItem('tractech_session_id');
        notifyAuthFailure(refreshError);
        return Promise.reject(refreshError);
      });
  }

  // If we get here, either:
  // - No config (can't retry)
  // - Already retried once (_retry is true)
  // - No refresh token available
  // Clear tokens on any unrecoverable 401 path to guarantee clean logout
  if (status === HttpStatusCode.Unauthorized) {
    // Also clear if this is a retried request that still got 401
    if (originalConfig?._retry) {
      localStorage.removeItem('tractech_token');
      localStorage.removeItem('tractech_refresh_token');
      localStorage.removeItem('tractech_session_id');
      notifyAuthFailure(error);
      return Promise.reject(error);
    }
    localStorage.removeItem('tractech_token');
    localStorage.removeItem('tractech_refresh_token');
    localStorage.removeItem('tractech_session_id');
    notifyAuthFailure(error);
  }

  return Promise.reject(error);
};

class Http {
  private get http(): AxiosInstance {
    return getAxiosInstance();
  }

  public request(config: AxiosRequestConfig) {
    return this.http.request(config);
  }

  public get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.http.get<T>(url, config);
  }

  public post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.http.post<T>(url, data, config);
  }

  public delete(url: string, config?: AxiosRequestConfig) {
    return this.http.delete(url, config);
  }

  public put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.http.put<T>(url, data, config);
  }

  public patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.http.patch<T>(url, data, config);
  }
}

export const http = new Http();
// Prefer AbortController at call sites (axios v1+)
// Keep isCancel for legacy callers if needed
export const isCancel = axios.isCancel;
